// טריגרים (19): "מתי" (לוח זמנים; בעתיד — אירוע) + "מה" (יצירה עם פרמטרים) + "בשם מי" (הבעלים).
// השורות של sys.trigger / sys.trigger_run, וקלט הכלים/REST. החישוב (cron, Asia/Jerusalem, describeHe) — packages/core/src/cron.ts.
import { z } from 'zod';
import { Id, Json, Ts, rowBase } from './common.ts';

/** 19 §3: מרווח מינימלי בין הפעלות (interval ≥ 10; cron — שתי הפעלות עוקבות ≥ 10 דק'). */
export const TRIGGER_MIN_MINUTES = 10;
/** 19 §1.1: תקרות (קונפיג בעתיד). */
export const TRIGGER_LIMITS = { perUser: 50, perInstance: 500 } as const;
export const TRIGGER_TZ = 'Asia/Jerusalem';

export const TriggerSchedule = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('cron'), expr: z.string().min(9).max(120), tz: z.literal(TRIGGER_TZ).default(TRIGGER_TZ) }),
  z.object({ kind: z.literal('interval'), minutes: z.number().int().min(TRIGGER_MIN_MINUTES).max(60 * 24 * 31) }),
]);
export type TriggerSchedule = z.infer<typeof TriggerSchedule>;

/** target.kind קיים לעתיד; היום רק יצירה (הוכרע — 19 §3). */
export const TriggerTarget = z.object({
  kind: z.literal('artifact'), slug: z.string().regex(/^[a-z][a-z0-9_]{1,60}$/), params: z.record(z.string(), z.unknown()).default({}),
  /** artifacts-report-lookup: התווית של מה שנבחר בשדה חיפוש ({param: labelHe}) — מועתקת ל-param_labels של הריצה המתוזמנת. */
  paramLabels: z.record(z.string(), z.string()).optional(),
});
export type TriggerTarget = z.infer<typeof TriggerTarget>;

export const TriggerPolicy = z.object({ overlap: z.literal('skip'), pauseAfterFailures: z.number().int().positive(), jitterS: z.number().int().min(0).max(300) });
export type TriggerPolicy = z.infer<typeof TriggerPolicy>;
export const DEFAULT_TRIGGER_POLICY: TriggerPolicy = { overlap: 'skip', pauseAfterFailures: 3, jitterS: 60 };

export const TriggerPausedReason = z.enum(['needs_consent', 'failures', 'target_missing', 'owner_inactive']);
export type TriggerPausedReason = z.infer<typeof TriggerPausedReason>;
export const TriggerRunStatus = z.enum(['started', 'done', 'failed', 'timeout', 'skipped_overlap', 'skipped_paused', 'skipped_missed']);
export type TriggerRunStatus = z.infer<typeof TriggerRunStatus>;

/** sys.trigger */
export const Trigger = z.object({
  ...rowBase,
  ownerUserId: Id,
  nameHe: z.string().min(1).max(80),
  source: z.enum(['schedule', 'event']),
  schedule: TriggerSchedule.nullable(),
  event: Json.nullable(),
  target: TriggerTarget,
  policy: TriggerPolicy,
  enabled: z.boolean(),
  pausedReason: TriggerPausedReason.nullable(),
  lastRunAt: Ts.nullable(),
  lastStatus: TriggerRunStatus.nullable(),
  nextRunAt: Ts.nullable(),
  /** כשלונות רצופים (מתאפס בהצלחה וב-resume). */
  failures: z.number().int().min(0),
  /** triggers-failure-notify-retry: ניסיון חוזר ממתין (מועד שנכשל בכשל חולף). */
  retryAt: Ts.nullable(),
  createdBy: Id,
  deletedAt: Ts.nullable(),
  /** data-access-rls (19 §3): admin בלבד — הריצה בזהות המשתמש הזה. */
  forUserId: Id.nullable(),
});
export type Trigger = z.infer<typeof Trigger>;

/** sys.trigger_run */
export const TriggerRun = z.object({
  ...rowBase,
  triggerId: Id,
  scheduledFor: Ts,
  firedAt: Ts,
  status: TriggerRunStatus,
  artifactRunId: Id.nullable(),
  reason: z.string().nullable(),
  errorHe: z.string().nullable(),
  finishedAt: Ts.nullable(),
  msTotal: z.number().int().nullable(),
  /** 1 = המועד, 2 = הניסיון החוזר. */
  attempt: z.number().int().min(1).max(2),
});
export type TriggerRun = z.infer<typeof TriggerRun>;

/** קלט schedule מהקורא (tz קבוע — לא נשלח). */
export const TriggerScheduleIn = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('cron'), expr: z.string().min(1).max(120) }).strict(),
  z.object({ kind: z.literal('interval'), minutes: z.number().int().positive() }).strict(),
]);

/** POST /api/triggers · trigger.create — strict: `key` (כישור) במקום `slug` → 400 (19 §4.8). */
export const CreateTrigger = z
  .object({
    nameHe: z.string().trim().min(1).max(80),
    slug: z.string().regex(/^[a-z][a-z0-9_]{1,60}$/, 'slug של יצירה'),
    params: z.record(z.string(), z.unknown()).optional(),
    /** artifacts-report-lookup: רק לפרמטרים עם lookup, ≤ 200 תווים (אחרת 400 invalid_param_labels). */
    paramLabels: z.record(z.string(), z.unknown()).optional(),
    schedule: TriggerScheduleIn,
    enabled: z.boolean().optional(),
    /** data-access-rls (19 §5 → §3): admin בלבד — הריצה בזהות המשתמש הזה (נתונים, מייל, היקפים); הבעלים נשאר admin. */
    forUserId: z.uuid().optional(),
  })
  .strict();
export type CreateTrigger = z.infer<typeof CreateTrigger>;

/** PATCH /api/triggers/:id · trigger.update */
export const UpdateTrigger = z
  .object({
    nameHe: z.string().trim().min(1).max(80).optional(), schedule: TriggerScheduleIn.optional(), params: z.record(z.string(), z.unknown()).optional(),
    paramLabels: z.record(z.string(), z.unknown()).optional(), enabled: z.boolean().optional(),
  })
  .strict();
export type UpdateTrigger = z.infer<typeof UpdateTrigger>;
